import type { DataGridColumnProps } from '../../../components/DataGrid/DataGrid';
import type { DataGridColumnOptionsProps } from '../../../components/DataGridControl/DataGridControl';
import type { GridFilters } from '../../../components/GridFiltersManager/filterDefinitions';
import type { PageChangeFn } from '../../../components/Pagination/Pagination';
import type {
  ContentObject,
  ContentTypeDefinition,
} from '../../../lib/flotiq-client/types/api-response';
import type { SortContentObjectsLocalStorage } from '../../../types/localStorage';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import {
  getObjectTitle,
  getTestProps,
  prepareColumns,
  setInitialGridOptions,
  strToChecksum,
} from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import CustomizableDataGrid from '../../../components/CustomizableDataGrid/CustomizableDataGrid';
import DateContainer from '../../../components/DateContainer/DateContainer';
import GridFiltersManager from '../../../components/GridFiltersManager/GridFiltersManager';
import LinkButton from '../../../components/LinkButton/LinkButton';
import Loader from '../../../components/Loader/Loader';
import StatusBar from '../../../components/StatusBar/StatusBar';
import StatusPill from '../../../components/StatusPill/StatusPill';

import { useContentTypes, useSearch } from '../../../hooks/api';
import { useImportSpace } from '../../../hooks/useImportSpace';
import useSelectedSpace from '../../../hooks/useSelectedSpace';

import { CaretRightIcon, PlusIcon } from '../../../images/shapes';

import { usePopulateSpaceModal } from '../../../layout/PopulateSpaceModal/usePopulateSpaceModal';

const CONTENT_TYPE_COLORS = [
  'lime' as const,
  'blue' as const,
  'red' as const,
  'orange' as const,
];

const CTD_PARAMS = { limit: 1000 };
const LAST_CHANGES_LIMIT = 10;
const FIELD_MAP: Record<string, string> = {
  title: 'internal.objectTitle',
  contentType: 'internal.contentType',
  updated_by: '__updated_by.name', // Leave it untill we have a proper way to handle updated_by field
};

const getSearchApiFilters = (filters: GridFilters) => {
  const searchFilters: Record<string, string | string[]> = {};

  Object.entries(filters).forEach(([field, { filter }]) => {
    if (filter == null || filter === '') return;

    if (field === 'contentType') {
      const contentTypes = Array.isArray(filter) ? filter : [filter];
      if (contentTypes.length) {
        searchFilters.content_type = contentTypes as string[];
      }
      return;
    }

    const apiField = FIELD_MAP[field] || field;
    if (!apiField) return;

    const filterValue = Array.isArray(filter)
      ? filter.map(String).join(',')
      : String(filter);
    if (filterValue) searchFilters[`filters[${apiField}]`] = filterValue;
  });

  return searchFilters;
};

const DashboardLatestUpdates = ({ testId }: { testId?: string }) => {
  const { t } = useTranslation();
  const { buildUrlWithSpace, space } = useSelectedSpace();
  const { openPopulateSpaceModal } = usePopulateSpaceModal();
  const { openImportModal } = useImportSpace();

  const sortStorageKey = `cms.home-latest-updates-sort-${space}`;
  const gridOptionsStorageKey = `cms.home-latest-updates-grid-state-${space}`;
  const filtersStorageKey = `cms.home-latest-updates-filters-${space}`;

  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useLocalStorageState<GridFilters>(
    filtersStorageKey,
    { defaultValue: {} },
  );

  const gridContainer = useRef<HTMLDivElement>(null);
  const [sort, setSort, { removeItem: removeSort }] =
    useLocalStorageState<SortContentObjectsLocalStorage>(sortStorageKey);
  const [gridOptions, setGridOptions, { removeItem: removeGridOptions }] =
    useLocalStorageState<DataGridColumnOptionsProps[]>(gridOptionsStorageKey);

  const { data: contentTypes, isLoading: contentTypesLoading } =
    useContentTypes(CTD_PARAMS);

  const searchParams = useMemo(
    () => ({
      q: searchValue || '*',
      limit: LAST_CHANGES_LIMIT,
      order_by: sort?.sortField
        ? FIELD_MAP[sort.sortField] || sort.sortField
        : 'internal.updatedAt',
      order_direction: sort?.sortOrder || 'desc',
      page: currentPage,
      ...getSearchApiFilters(filters),
    }),
    [currentPage, filters, searchValue, sort?.sortField, sort?.sortOrder],
  );

  const {
    data: searchData,
    isLoading: searchLoading,
    pagination,
    reload,
  } = useSearch(searchParams);

  const handlePageChange: PageChangeFn = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleFiltersChange = (nextFilters: GridFilters) => {
    setFilters(nextFilters);
    setCurrentPage(1);
  };

  const handleResetGrid = useCallback(() => {
    setSort(undefined);
    removeSort();
    removeGridOptions();
    setGridOptions(undefined);
    setFilters({});
    setSearchValue('');
    setCurrentPage(1);
  }, [removeGridOptions, removeSort, setFilters, setGridOptions, setSort]);

  const contentTypesDictionary = contentTypes.reduce(
    (acc, ctd) => {
      acc[ctd.name] = ctd;
      return acc;
    },
    {} as Record<string, ContentTypeDefinition>,
  );

  const getCtdLink = (contentType: ContentTypeDefinition) => {
    if (!contentType || (contentType.internal && contentType.name !== '_media'))
      return '';
    return contentType.name === '_media'
      ? buildUrlWithSpace('media')
      : buildUrlWithSpace(`content-type-objects/${contentType.name}`);
  };

  const latestObjects = searchData.map((element) => {
    const contentTypeDefinition =
      contentTypesDictionary[element.item.internal.contentType];

    return {
      title: getObjectTitle(element.item, contentTypeDefinition),
      contentType: {
        name: element.item.internal.contentType,
        label:
          contentTypeDefinition?.label || element.item.internal.contentType,
        link: getCtdLink(contentTypeDefinition),
      },
      link: buildUrlWithSpace(
        element.item.internal.contentType === '_media'
          ? `media/edit/${element.item.id}`
          : `content-type-objects/edit/${element.item.internal.contentType}/${element.item.id}`,
      ),
      updated_by: element.item.__updated_by?.name || 'N/A',
      internal: element.item.internal,
    };
  });

  const hasActiveSearchCriteria =
    Boolean(searchValue.trim()) ||
    Object.keys(getSearchApiFilters(filters)).length > 0;

  const columnDefinitions = useMemo<DataGridColumnProps[]>(
    () => [
      {
        accessor: 'internal.status',
        label: t('Global.Status'),
        baseWidth: 120,
        minWidth: 100,
        resizable: true,
        filterInputType: 'internal.status',
        render: (status: ContentObject['internal']['status']) => (
          <StatusPill
            containerClasses="inline-flex items-center justify-center"
            color={status}
            status={t(`ObjectStatus.Status.${status}`)}
            size="sm"
          />
        ),
        sortable: true,
        showByDefault: true,
      },
      {
        accessor: 'title',
        label: t('Global.ObjectTitle'),
        minWidth: 140,
        resizable: true,
        filterInputType: 'text',
        render: (title: string) => (
          <span className="truncate min-w-0">{title}</span>
        ),
        sortable: true,
      },
      {
        accessor: 'contentType',
        label: t('Global.ContentType'),
        minWidth: 140,
        resizable: true,
        filterInputType: 'internal.contentType',
        filterInputOptions: [
          ...contentTypes.map((contentType) => ({
            value: contentType.name,
            label: contentType.label || contentType.name,
          })),
          ...(!contentTypes.some((contentType) => contentType.name === '_media')
            ? [{ value: '_media', label: t('Global.Media') }]
            : []),
        ],
        render: (data: (typeof latestObjects)[number]['contentType']) => (
          <StatusPill
            status={data.label}
            color={CONTENT_TYPE_COLORS[strToChecksum(data.name) % 4]}
            containerClasses={
              'flex items-center h-full text-xs lg:text-sm h-6 lg:h-7 ' +
              'group-hover:underline group-hover:shadow-lg hover:underline'
            }
            {...getTestProps(testId, `${data.name}-ctd`, 'testId')}
          />
        ),
        sortable: true,
      },
      {
        accessor: 'internal.updatedAt',
        label: t('Global.UpdatedAt'),
        baseWidth: 160,
        minWidth: 140,
        resizable: true,
        filterInputType: 'dateTime',
        render: (data: string) => <DateContainer date={data} isBold={false} />,
        sortable: true,
        showByDefault: true,
      },
      {
        accessor: 'updated_by',
        label: t('Global.UpdatedBy'),
        minWidth: 200,
        resizable: true,
        render: (data: string) => (
          <span className="truncate min-w-0">{data}</span>
        ),
        sortable: false, // UpdatedBy column should not be sortable or filterable for now
      },
      {
        accessor: 'internal.publishedAt',
        label: t('PublicationSchedule.Date'),
        minWidth: 140,
        resizable: true,
        showByDefault: false,
        filterInputType: 'dateTime',
        render: (data: string) => <DateContainer date={data} isBold={false} />,
        sortable: true,
      },
    ],
    [contentTypes, t, testId],
  );

  const [columns, setColumns] =
    useState<DataGridColumnProps[]>(columnDefinitions);

  const getDefinedColumns = useCallback(
    (
      gridOptionsById: Record<string, DataGridColumnOptionsProps> | undefined,
      hiddenColumnsById: Record<string, boolean | undefined>,
    ) =>
      Object.fromEntries(
        columnDefinitions.map((column) => {
          const savedOption = gridOptionsById?.[column.accessor];
          return [
            column.accessor,
            {
              ...column,
              width: savedOption?.width ?? column.width,
              hide: hiddenColumnsById[column.accessor] ?? !!column.hide,
            },
          ];
        }),
      ),
    [columnDefinitions],
  );

  const handleInitialGridOptions = useCallback(
    (cols: DataGridColumnProps[]) => {
      setInitialGridOptions(
        cols,
        100,
        (gridContainer.current?.offsetWidth || 0) - 58,
        setGridOptions,
        setColumns,
      );
    },
    [setGridOptions],
  );

  useEffect(() => {
    prepareColumns(
      gridOptions,
      t,
      testId || '',
      setGridOptions,
      handleInitialGridOptions,
      setColumns,
      getDefinedColumns,
    );
  }, [
    getDefinedColumns,
    gridOptions,
    handleInitialGridOptions,
    setGridOptions,
    t,
    testId,
  ]);

  const getRowLinkOptions = (
    accessor: string,
    rowData: (typeof latestObjects)[number],
  ) =>
    accessor === 'contentType'
      ? { to: rowData.contentType.link }
      : { to: rowData.link };

  return (
    <section>
      <h2 className="text-xl dark:text-slate-200">
        {t('Global.YourLatestUpdates')}
      </h2>

      <div className="mt-2 overflow-hidden">
        {(latestObjects.length > 0 || hasActiveSearchCriteria) && (
          <GridFiltersManager
            columns={columns}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            onResetGrid={handleResetGrid}
            showSearchInput
            testId={testId}
          />
        )}
        <div id="latest_updates" ref={gridContainer}>
          <CustomizableDataGrid
            columns={columns}
            setColumns={setColumns}
            data={latestObjects}
            isLoading={searchLoading || contentTypesLoading}
            sort={sort?.sortField}
            sortOrder={sort?.sortOrder}
            sortingLocalStorageKey={sortStorageKey}
            optionsLocalStorageKey={gridOptionsStorageKey}
            setCurrentPage={handlePageChange}
            statusBar={
              <StatusBar
                rows={pagination.count}
                currentPage={currentPage}
                pagesCount={pagination.total_pages}
                handlePageChange={handlePageChange}
                handleDataUpdate={reload}
                resultsFrom={
                  (pagination.current_page - 1) * LAST_CHANGES_LIMIT + 1
                }
                resultsTo={
                  (pagination.current_page - 1) * LAST_CHANGES_LIMIT +
                  pagination.count
                }
                resultsTotalCount={pagination.total_count}
              />
            }
            noDataInfo={
              hasActiveSearchCriteria ? (
                <span className="flex items-center justify-center w-full h-full dark:text-slate-200">
                  {t('ObjectsOfType.NoMatchingData')}
                </span>
              ) : (
                <div className="px-5 w-full h-full flex flex-col gap-6 items-center justify-center">
                  <p className="text-center text-lg dark:text-slate-200">
                    <strong>{t('ObjectsOfType.NoMatchingData')}</strong>
                    <br />
                    <span className="font-normal">
                      {t('Global.CreateYourProjectStructure')}
                    </span>
                  </p>

                  <div className="flex flex-wrap justify-center gap-4 lg:gap-8">
                    <LinkButton
                      link={buildUrlWithSpace('content-type-definitions')}
                      buttonColor="borderlessBlue"
                      additionalClasses="hover:opacity-100 hover:underline"
                      iconImage={<PlusIcon className="size-2.5" />}
                    >
                      {t('Home.CreateContentType')}
                    </LinkButton>
                    <Button
                      onClick={openPopulateSpaceModal}
                      buttonColor="borderlessBlue"
                      additionalClasses="hover:opacity-100"
                      iconImage={<CaretRightIcon className="size-2.5" />}
                    >
                      {t('Home.TryDemo')}
                    </Button>
                    <Button
                      buttonColor="borderlessBlue"
                      additionalClasses="hover:opacity-100"
                      iconImage={<CaretRightIcon className="size-2.5" />}
                      onClick={openImportModal}
                    >
                      {t('Home.ImportData')}
                    </Button>
                  </div>
                </div>
              )
            }
            loadingIcon={<Loader size="small" type="spinner-grid" />}
            getRowLinkOptions={getRowLinkOptions}
            fillHeight={false}
            autoHeight
            {...getTestProps(testId, 'grid', 'testId')}
          />
        </div>
      </div>
    </section>
  );
};

export default DashboardLatestUpdates;
